import React from 'react';
import { T, useTranslate } from '@tolgee/react';
import { TrackingStepView } from '../orderTracking.interface';
import { TRACKING_ROLE_LABELS } from '../orderTracking.constants';
import { formatDateTime } from '../orderTracking.utils';
import { cn } from '../../../../lib/utils';
import { Checkmark } from '../../../../assets/Checkmark';
import DocumentIcon from '../../../../assets/DocumentIcon';

interface TruckStepsProps {
	steps: TrackingStepView[];
	onOpenDocuments?: () => void;
}

const TruckSteps: React.FC<TruckStepsProps> = ({ steps, onOpenDocuments }) => {
	const { t } = useTranslate();

	const roleLabel = (role?: string) => {
		if (!role) return t('orderTracking.roles.driver', 'Driver');
		const known = TRACKING_ROLE_LABELS[role.toLowerCase()];
		return known ? t(known.labelKey, known.defaultLabel) : role;
	};

	return (
		<div className="pt-1 pb-5 flex flex-col">
			{steps.map((step, index) => {
				const isLast = index === steps.length - 1;
				const reached = !step.upcoming;
				const dateLabel = formatDateTime(step.completedAt);

				return (
					<div
						key={step.key}
						className="grid grid-cols-[24px,minmax(0,1fr)] gap-[14px]"
					>
						<div className="flex flex-col items-center">
							{step.done ? (
								<div className="w-6 h-6 rounded-full grid place-items-center bg-track-teal border-2 border-solid border-track-teal flex-none box-border">
									<Checkmark color="#FFFFFF" width={12} height={9} />
								</div>
							) : step.current ? (
								<div className="w-6 h-6 rounded-full grid place-items-center bg-track-amber-soft border-2 border-solid border-track-amber flex-none box-border">
									<span className="w-2 h-2 rounded-full bg-track-amber" />
								</div>
							) : (
								<div className="w-6 h-6 rounded-full bg-white border-2 border-solid border-track-line flex-none box-border" />
							)}
							{!isLast ? (
								<div
									className={cn(
										'flex-1 w-[2px] min-h-[18px]',
										step.done ? 'bg-track-teal' : 'bg-track-line',
									)}
								/>
							) : null}
						</div>

						<div className="pt-[2px] pb-[18px]">
							{!reached ? (
								<div className="text-sm leading-[1.3] text-track-muted">
									{step.label}
								</div>
							) : (
								<>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<div className="text-sm font-medium leading-[1.3] text-track-ink">
												{step.label}
											</div>
											{dateLabel || step.current ? (
												<div className="text-[12.5px] text-track-muted mt-[2px]">
													{dateLabel}
													{step.current ? (
														<>
															{' · '}
															<T keyName="orderTracking.currentStep">
																current step
															</T>
														</>
													) : null}
												</div>
											) : null}
										</div>
										{step.actor &&
										step.actor.role.toLowerCase() !== 'driver' ? (
											<div className="flex flex-col items-end gap-[2px] flex-none max-w-[45%]">
												<span className="text-xs leading-[1.3] text-track-muted whitespace-nowrap">
													{roleLabel(step.actor.role)}
												</span>
												<span className="text-[13px] font-semibold text-track-slate whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
													{step.actor.name}
												</span>
											</div>
										) : step.documentsCount ? (
											<button
												type="button"
												onClick={onOpenDocuments}
												className="flex-none inline-flex items-center gap-[6px] h-[26px] px-[10px] rounded-full border border-solid border-track-line bg-white font-[inherit] text-xs font-semibold text-track-indigo cursor-pointer transition hover:bg-track-hover hover:border-[#9A9ABF]"
											>
												<DocumentIcon color="#484880" width={10} height={12} />
												<T
													keyName="orderTracking.documentsCount"
													params={{ count: step.documentsCount }}
												>
													{'{count} documents'}
												</T>
											</button>
										) : null}
									</div>
								</>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default TruckSteps;
